import { useState } from "react";
import { Link, router } from "@inertiajs/react";
import { ArrowLeft, Mail, RefreshCw, ShieldAlert, ShieldCheck, XCircle } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";
import { OutboundEntry, OutboundList, STATUS_COLORS, STATUS_LABELS, fmt } from "./_types";

interface Props {
  list: OutboundList;
  entries: OutboundEntry[];
  is_admin: boolean;
  ai_assist: boolean;
}

const DNC_RESULT_CONFIG: Record<string, { label: string; className: string }> = {
  pending:    { label: "Pending",    className: "bg-gray-100 text-gray-500" },
  clear:      { label: "Clear",      className: "bg-green-100 text-green-700" },
  blocked:    { label: "Blocked",    className: "bg-red-100 text-red-600" },
  overridden: { label: "Overridden", className: "bg-yellow-100 text-yellow-700" },
};

const VARS = ["first_name", "last_name", "firm_name", "location", "practice_area", "jd_year"];

function ComposeSection({ list, aiAssist }: { list: OutboundList; aiAssist: boolean }) {
  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");
  const [tone, setTone]       = useState("conservative");
  const [open, setOpen]       = useState(false);

  function submit() {
    router.post(`/outbound/lists/${list.id}/blast`, {
      blast: { subject, body_template: body, tone },
    });
  }

  if (!open) {
    return (
      <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white" onClick={() => setOpen(true)}>
        <Mail className="w-3.5 h-3.5 mr-1" /> Create Campaign
      </Button>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3 mt-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-gray-700">Compose Email Campaign</h3>
        <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full text-sm border border-gray-200 rounded px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-green-500"
          placeholder="Subject — use {{first_name}}, {{firm_name}} tokens"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Body</label>
        <p className="text-xs text-gray-400 mb-1">
          Tokens: {VARS.map((v) => <code key={v} className="bg-gray-100 px-1 rounded mr-1">{`{{${v}}}`}</code>)}
        </p>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className="w-full text-sm border border-gray-200 rounded px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-green-500 resize-y"
          placeholder={`Hi {{first_name}},\n\nI wanted to reach out...`}
        />
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-500">Tone:</span>
        {["conservative", "assertive"].map((t) => (
          <button
            key={t}
            onClick={() => aiAssist && setTone(t)}
            disabled={!aiAssist}
            title={!aiAssist ? "Requires AI Assist (enable in Settings)" : undefined}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
              !aiAssist
                ? "border-gray-100 text-gray-300 cursor-not-allowed"
                : tone === t
                  ? "bg-green-700 text-white border-green-700"
                  : "border-gray-200 text-gray-500 hover:border-green-400"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
        {!aiAssist && <span className="text-xs text-gray-400 italic">Requires AI Assist</span>}
      </div>
      <Button
        onClick={submit}
        disabled={!subject.trim() || !body.trim()}
        className="w-full bg-green-700 hover:bg-green-800 text-white disabled:opacity-50"
      >
        Create Campaign &rarr; Review
      </Button>
    </div>
  );
}

export default function OutboundShow({ list, entries, is_admin, ai_assist }: Props) {
  const canCheckDnc = list.status === "uploaded" || list.status === "checked";
  const cleanEntries   = entries.filter((e) => e.dnc_result === "clear" || e.dnc_result === "overridden");
  const blockedEntries = entries.filter((e) => e.dnc_result === "blocked");

  function checkDnc() {
    router.post(`/outbound/lists/${list.id}/check_dnc`);
  }

  function abandonList() {
    if (!confirm(`Mark "${list.name}" as not proceeding? It will be removed from the active list but kept for records.`)) return;
    router.patch(`/outbound/lists/${list.id}/abandon`);
  }

  return (
    <AppLayout title={list.name}>
      <Link href="/outbound" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Outbound Lists
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{list.name}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Uploaded {fmt(list.uploaded_at ?? list.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[list.status] ?? "bg-gray-100 text-gray-500"}`}>
            {STATUS_LABELS[list.status] ?? list.status}
          </span>
          {canCheckDnc && (
            <Button size="sm" variant="outline" onClick={checkDnc}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Run DNC Check
            </Button>
          )}
          {list.status === "checked" && cleanEntries.length > 0 && (
            <ComposeSection list={list} aiAssist={ai_assist} />
          )}
          {list.status !== "abandoned" && (
            <Button size="sm" variant="outline" onClick={abandonList} className="text-gray-400 hover:text-red-500 hover:border-red-300">
              <XCircle className="w-3.5 h-3.5 mr-1" /> Not Proceeding
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {list.status !== "uploaded" && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{list.total_count}</div>
            <div className="text-xs text-gray-500 mt-0.5">Total</div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-100 p-4 text-center">
            <div className="text-2xl font-bold text-green-700">{cleanEntries.length}</div>
            <div className="text-xs text-green-600 mt-0.5 flex items-center justify-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Clear
            </div>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-100 p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{blockedEntries.length}</div>
            <div className="text-xs text-red-500 mt-0.5 flex items-center justify-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Blocked
            </div>
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Firm</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">DNC</th>
              {list.status !== "uploaded" && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Reason</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.map((e) => {
              const dnc = DNC_RESULT_CONFIG[e.dnc_result] ?? DNC_RESULT_CONFIG.pending;
              const rowClass = e.dnc_result === "blocked"
                ? "bg-red-50/40"
                : e.dnc_result === "clear" || e.dnc_result === "overridden"
                  ? "bg-green-50/20"
                  : "";
              return (
                <tr key={e.id} className={`${rowClass} hover:bg-gray-50`}>
                  <td className="px-4 py-2.5 text-sm text-gray-900">
                    {[e.first_name, e.last_name].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{e.email}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{e.firm_name || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{e.location || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${dnc.className}`}>
                      {dnc.label}
                    </span>
                  </td>
                  {list.status !== "uploaded" && (
                    <td className="px-4 py-2.5 text-xs text-gray-400">
                      {e.dnc_result === "overridden"
                        ? <span className="text-yellow-600">Override: {e.override_reason}</span>
                        : e.dnc_reason || "—"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
